import { uniqBy, sortBy } from 'lodash-es'
import { meterToMillimeter } from '@utils'
import { getPlateTypeLabel } from '../constants'

export const buildProducerOptions = (
  data: PlateCrossSection[],
): { value: string; label: string }[] => {
  const values = data.map(({ producer }) => ({
    value: producer.trim(),
    label: producer,
  }))

  return uniqBy(values, 'value')
}

export const buildPlateTypeOptions = (
  data: PlateCrossSection[],
  producer: string,
): { value: string; label: string }[] => {
  const dataByProducer = data.filter(plate => plate.producer === producer)

  const values = dataByProducer.map(({ material }) => ({
    value: String(material.kind),
    label: getPlateTypeLabel(String(material.kind)),
  }))

  return sortBy(uniqBy(values, 'value'), ['label'])
}

export const buildMaterialOptions = (
  data: PlateCrossSection[],
  producer: string,
  plateType: string,
): { value: string; label: string }[] => {
  const dataByType = data.filter(
    plate => plate.producer === producer && String(plate.material.kind) === plateType,
  )

  const uniqueMaterials = uniqBy(
    dataByType.map(({ material }) => material),
    'identifier',
  )

  return sortBy(uniqueMaterials, ['thickness', 'identifier']).map(material => ({
    value: material.identifier,
    label: `${material.identifier} (${meterToMillimeter(material.thickness)} mm)`,
  }))
}

export const buildFormatOptions = (
  data: PlateCrossSection[],
  producer: string,
  materialIdentifier: string,
): { value: PlateCrossSection; label: string }[] => {
  const dataByMaterial = data.filter(
    plate => plate.producer === producer && plate.material.identifier === materialIdentifier,
  )

  const values = dataByMaterial.map(value => ({
    value: value,
    label: `${meterToMillimeter(value.shape_x.width)} x ${meterToMillimeter(
      value.shape_y.width,
    )} mm`,
  }))

  return sortBy(uniqBy(values, 'label'), [
    ({ value }) => value.shape_x.width,
    ({ value }) => value.shape_y.width,
  ])
}

export const buildFastenerProducerOptions = (
  data: Fastener[],
): { value: string; label: string }[] => {
  const values = data.map(({ producer }) => ({
    value: producer.trim(),
    label: producer,
  }))

  return uniqBy(values, 'value')
}

export const buildFastenerOptions = (
  data: Fastener[],
  producer: string,
): { value: Fastener; label: string }[] => {
  const dataByProducer = data.filter(fastener => fastener.producer === producer)

  return sortBy(dataByProducer, ['unique_name', 'length']).map(fastener => ({
    value: fastener,
    label: `${fastener.unique_name} ${meterToMillimeter(fastener.length)}mm`,
  }))
}
